import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import constVars from '~/constants/constVars';
import { languageIsValidForMarket } from '~/helpers/market.helper';
import { isInIframe } from '~/iframeMode';
import { RootState } from '~/store';
import { history } from '~/constants';
import { setLanguage } from '~/store/slices/intl.slice';

export function useAppInitEffects() {
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const { market } = useSelector((state: RootState) => state.intl);

  useEffect(() => {
    const url = new URL(window.location.href);

    const ssLandingUrl = sessionStorage.getItem('landingUrl');
    if (!ssLandingUrl) {
      sessionStorage.setItem('landingUrl', window.location.href);
    }

    if (!isInIframe) {
      url.searchParams.delete('iframe');
    }

    const redirectedFromMyQred = url.searchParams.get('redirectedFromMyQred');

    if (redirectedFromMyQred) {
      posthog.capture(
        'onboarding_user_redirected_from_my_qred_onboarding_route_to_onboarding_subdomain',
        {
          from: document.referrer,
          to: window.location.href,
        }
      );

      // If the user is redirected from My Qred, we want to remove the query param
      url.searchParams.delete('redirectedFromMyQred');
    }

    // if the user is redirected from My Qred through the RedirectToOnboardingDomain component,
    // we want to use the originalReferrer URL from the query params
    const originalReferrerUrlFromMyQred = url.searchParams.get(
      'originalReferrer'
    );
    const ssReferrerUrl = sessionStorage.getItem('referrerUrl');
    // To prevent overwriting sessionStorage after redirecting from auth0
    if (
      !ssReferrerUrl &&
      (originalReferrerUrlFromMyQred || document.referrer)
    ) {
      const referrer = originalReferrerUrlFromMyQred
        ? decodeURI(originalReferrerUrlFromMyQred)
        : document.referrer;
      sessionStorage.setItem('referrerUrl', referrer);

      /**
       * This logic is relevant to login page only.
       * We want to move it to the login component, but have not found a way yet.
       * The reason for this logic is to "reset" local storage market values when coming
       * from a new document referrer.
       */
      if (window.location.pathname === `/${constVars.ROUTE_LOGIN}`) {
        localStorage.removeItem('qredMarket');
        localStorage.removeItem('qredLoginLanguage');
      }
    }

    if (originalReferrerUrlFromMyQred) {
      url.searchParams.delete('originalReferrer');
    }

    const languageUrlParam = url.searchParams.get('language');
    if (
      languageUrlParam &&
      languageIsValidForMarket(languageUrlParam, market)
    ) {
      dispatch(setLanguage(languageUrlParam));
      url.searchParams.delete('language');
    }

    if (url.search !== window.location.search) {
      history.replace({
        search: url.search,
      });
    }
  }, []);

  useEffect(() => {
    if (!isInIframe) return;

    return history.listen((location) => {
      const params = new URLSearchParams(location.search);
      if (!params.has('iframe')) {
        params.set('iframe', 'true');
        history.replace({ ...location, search: `?${params.toString()}` });
      }
    });
  }, []);
}
